import { MouseEvent, useCallback } from 'react'
import { useQuery } from '@apollo/client/react'
import clsx from 'clsx'
import { AdminAPI, getFragment, SharedAPI } from '@guestgoodie/graphql'
import { goodie as goodieHelpers } from '@guestgoodie/helpers'
import { HugeiconsIcon } from '@hugeicons/react'
import { useLocation } from 'wouter'
import { SquareIcon } from '@hugeicons/core-stroke-rounded'
import { useLingui } from '@lingui/react/macro'

import {
  CheckmarkSquare02Icon,
  Megaphone01Icon
} from '@hugeicons/core-solid-rounded'

import * as Table from '@/components/ui/Table'
import { useTypeToLabel } from '../utils/helpers'
import { Permission, useCheckPermission } from '@/features/auth'

interface RowProps {
  id: number
  isDisabled: boolean
  isSelected: boolean
  hideButtons: boolean
  hideCheckbox: boolean
  shouldSelectInsteadOfEdit: boolean
  onSelect: () => void
}

export default function GoodieRow({
  id,
  isDisabled,
  isSelected,
  hideButtons,
  hideCheckbox,
  shouldSelectInsteadOfEdit,
  onSelect
}: RowProps) {
  const goodieTypeToLabel = useTypeToLabel()
  const { t } = useLingui()
  const canCreatePromotion = useCheckPermission([Permission.PROMOTION_CREATE])

  const { data } = useQuery(AdminAPI.goodie.GET, {
    variables: { id }
  })

  const goodie = getFragment(SharedAPI.goodie.GOODIE_FRAGMENT, data?.getGoodie)

  const [, navigate] = useLocation()

  const onEdit = useCallback(() => {
    if (shouldSelectInsteadOfEdit) {
      onSelect()
      return
    }

    navigate(`/goodie/${goodie?.token}`)
  }, [goodie, shouldSelectInsteadOfEdit, navigate, onSelect])

  const onPromote = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      navigate(`/create-promotion/${goodie?.token}`)
    },
    [goodie, navigate]
  )

  const onSelectProxy = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      onSelect()
    },
    [onSelect]
  )

  if (!goodie) {
    return <Table.Row />
  }

  return (
    <Table.Row
      className={clsx(
        'cursor-pointer',
        isSelected && 'is-active',
        isDisabled && 'pointer-events-none opacity-50'
      )}
      onClick={onEdit}
      onEnter={onEdit}
    >
      {!hideCheckbox && (
        <Table.Cell>
          <div className="flex flex-row items-center justify-center">
            <button onClick={onSelectProxy} tabIndex={0}>
              {isSelected && (
                <HugeiconsIcon
                  icon={CheckmarkSquare02Icon}
                  className="text-primary"
                />
              )}
              {!isSelected && (
                <HugeiconsIcon icon={SquareIcon} className="text-primary" />
              )}
            </button>
          </div>
        </Table.Cell>
      )}
      <Table.Cell>{goodie.name}</Table.Cell>
      <Table.Cell>{goodieHelpers.toInfoFromGoodieFragment(goodie)}</Table.Cell>
      <Table.Cell>{goodieHelpers.toPriceFromGoodieFragment(goodie)}</Table.Cell>
      <Table.Cell>{goodieTypeToLabel[goodie.type]}</Table.Cell>
      <Table.Cell>
        {!hideButtons && canCreatePromotion && (
          <div className="flex flex-row gap-5">
            <button tabIndex={0} aria-label={t`Promote`} onClick={onPromote}>
              <HugeiconsIcon icon={Megaphone01Icon} className="size-5" />
            </button>
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  )
}
